import React, { ReactNode, useState, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import { ChipsInput, CustomButton } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './languages-tab.scss';

// Store entities
import {
  updateCompanyLanguages
} from '../../../../store/company/company.thunks';
import { getCoreState } from '../../../../store/core/core.selectors';

// Models
import { getCompanyState } from '../../../../store/company/company.selectors';

export type TLanguagesTabRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TLanguagesTabProps = {};

export const LanguagesTab = forwardRef<TLanguagesTabRef, TLanguagesTabProps>((
    {}: TLanguagesTabProps, ref
  ) => {

    const [form] = Form.useForm<{
      languages: string[];
    }>();

    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [savingChanges, setSavingChanges] = useState<boolean>(false);

    const {allLanguages} = useSelector(getCompanyState);
    const {languages} = useSelector(getCoreState);

    const dispatch = useDispatch<any>();

    const initialValues = languages.list.map(s => s.name);

    const generateOptions = (options: {name: string, id?: number, code?: string}[]): {
      label: ReactNode,
      value: string,
      disabled?: boolean,
    }[] => {
      return options.map((option) => (
        {label: option.name, value: option.name})
      )
    };

    const languagesOptions = generateOptions(allLanguages);

    const onValuesChange = (_: {languages: string[]}, {languages}: {languages: string[]}): void => {
      const hasChanges = !languages.every(lang => initialValues.includes(lang)) || languages.length !== initialValues.length;
      setHasChanges(hasChanges);
    }

    const saveChanges = async (): Promise<void> => {
      const {languages} = form.getFieldsValue();
      setSavingChanges(true);
      const {payload} = await dispatch(updateCompanyLanguages(
        allLanguages.filter(lang => languages.includes(lang.name)).map(lang => lang.code)
      ));
      setSavingChanges(false);
      if (!payload) return;
      setHasChanges(false);
      form.resetFields();
    }

    useImperativeHandle(ref, () => ({
      isHasChanges(): boolean {
        return hasChanges;
      },
      resetState(): void {
        form.setFieldValue('languages', initialValues);
        setHasChanges(false);
        setSavingChanges(false);
      }
    }));

    return (
      <div className="languages-tab">
        <div className="languages-tab--description business--description">
          <p>Unlock your Consultants' multilingual talents!<br />
            In this section, you can select the languages that your Consultants are proficient in. If you have Consultants
            who are fluent in multiple languages, you can choose all the languages they speak. This improves search accuracy
            and enhances client satisfaction by helping clients find the perfect language match for their needs.</p>

          <p>If your business primarily operates in one language, and your clients also primarily use the same language,
            filling out this field is optional.</p>

          <p>Please note that the languages added here will automatically appear in the Consultant Addition Form and will
            be used for filtering the Consultant list in the "Consultants" section of your Admin Panel and in the Consultant
            list in the applications for your clients.</p>
        </div>
        <Form className="languages-tab--body business--body-items" form={form} onValuesChange={onValuesChange}>
          {!languages.isLoad &&
            <div className="body-item chips-items">
              <Form.Item name="languages" initialValue={initialValues}>
                <ChipsInput options={languagesOptions}
                            placeholder={'Language(s)'}
                            hint={'Start typing to add a language to your list'}
                />
              </Form.Item>

              {hasChanges && <div className="chips-items--actions">
                <CustomButton size={'small'} loading={savingChanges} text={'Save'} onClick={saveChanges}/>
              </div>}
            </div>
          }
        </Form>
      </div>
    );
  }
)
